import type { ReactElement } from "react";

import {
  ChevronLeft,
  ChevronRight,
  Maximize,
  ZoomIn,
  ZoomOut,
} from "@/shared/foundation/icons";
import {
  RobotoButton,
  Toggle,
  ToggleGroup,
  Tooltip,
} from "@/shared/foundation/primitives";

import type { StepDirection } from "./layout";
import { activePreset, presetWindow, SPAN_PRESETS } from "./spanPresets";
import { useTimelineContext } from "./TimelineContext";
import styles from "./TimelineControls.module.css";
import { MIN_SPAN, spanOf, type TimeZoneMode } from "./window";
import { WindowRange } from "./WindowRange";

const ZONES: readonly { value: TimeZoneMode; label: string; spoken: string }[] =
  [
    { value: "local", label: "Local", spoken: "Local time" },
    { value: "utc", label: "UTC", spoken: "UTC" },
  ];

interface TimelineControlsProps {
  /** The clock times are read on; the toggle's pressed side. */
  tz: TimeZoneMode;
  /** The Local/UTC toggle was switched. Without it the toggle is not shown. */
  onTzChange?: (tz: TimeZoneMode) => void;
  /** Previous / next was pressed; the ← → keys call the same handler. */
  onStep: (direction: StepDirection) => void;
  /** False when a step that way is known to find no item; that button is disabled. */
  canStep: Record<StepDirection, boolean>;
}

const STEPS: readonly {
  direction: StepDirection;
  label: string;
  icon: ReactElement;
}[] = [
  { direction: -1, label: "Previous session", icon: <ChevronLeft /> },
  { direction: 1, label: "Next session", icon: <ChevronRight /> },
];

/**
 * Timeline controls: previous / next, the window's range (jump to a date),
 * span presets, zoom, and the Local/UTC toggle (only when `onTzChange` is given)
 */
export function TimelineControls({
  tz,
  onTzChange,
  onStep,
  canStep,
}: TimelineControlsProps) {
  const { timeWindow, extent, setTimeWindow, zoomIn, zoomOut, fitAll } =
    useTimelineContext();
  const active = activePreset(timeWindow);
  const atWidest = spanOf(timeWindow) >= spanOf(extent);
  const atNarrowest = spanOf(timeWindow) <= MIN_SPAN;

  const pickPreset = (values: string[]) => {
    // Unpressing the pressed preset reports no value; the window stays.
    const value = values[0];
    const preset = SPAN_PRESETS.find((candidate) => candidate.value === value);
    if (!preset) {
      return;
    }
    const { span } = preset;
    setTimeWindow((window) => presetWindow(window, span));
  };

  const pickZone = (values: string[]) => {
    // Unpressing the pressed zone reports no value; the zone stays.
    const zone = ZONES.find((candidate) => candidate.value === values[0]);
    if (zone) {
      onTzChange?.(zone.value);
    }
  };

  return (
    <div className={styles.controls} role="toolbar" aria-label="Time window">
      <div className={styles.actions}>
        <div className={styles.group}>
          {STEPS.map((stepButton) => (
            <Tooltip key={stepButton.direction}>
              <Tooltip.Trigger
                render={
                  <RobotoButton
                    variant="ghost"
                    size="sm"
                    icon={stepButton.icon}
                    aria-label={stepButton.label}
                    disabled={!canStep[stepButton.direction]}
                    // Stepping to the first or last item disables the
                    // button just pressed; it keeps focus, so a keyboard
                    // user can step back without tabbing in again.
                    focusableWhenDisabled
                    onClick={() => onStep(stepButton.direction)}
                  />
                }
              />
              <Tooltip.Content>{stepButton.label}</Tooltip.Content>
            </Tooltip>
          ))}
        </div>
        <WindowRange />
      </div>
      <div className={styles.actions}>
        <ToggleGroup
          size="sm"
          aria-label="Span"
          value={active === null ? [] : [active]}
          onValueChange={pickPreset}
        >
          {SPAN_PRESETS.map((preset) => (
            <Toggle
              key={preset.value}
              value={preset.value}
              aria-label={`Last ${preset.label}`}
            >
              {preset.label}
            </Toggle>
          ))}
        </ToggleGroup>
        <div className={styles.group}>
          <Tooltip>
            <Tooltip.Trigger
              render={
                <RobotoButton
                  variant="ghost"
                  size="sm"
                  icon={<ZoomIn />}
                  aria-label="Zoom in"
                  disabled={atNarrowest}
                  onClick={zoomIn}
                />
              }
            />
            <Tooltip.Content>Zoom in</Tooltip.Content>
          </Tooltip>
          <Tooltip>
            <Tooltip.Trigger
              render={
                <RobotoButton
                  variant="ghost"
                  size="sm"
                  icon={<ZoomOut />}
                  aria-label="Zoom out"
                  disabled={atWidest}
                  onClick={zoomOut}
                />
              }
            />
            <Tooltip.Content>Zoom out</Tooltip.Content>
          </Tooltip>
          <RobotoButton
            variant="ghost"
            size="sm"
            icon={<Maximize />}
            disabled={atWidest}
            onClick={fitAll}
          >
            Fit all
          </RobotoButton>
        </div>
        {onTzChange && (
          <ToggleGroup
            size="sm"
            aria-label="Time zone"
            value={[tz]}
            onValueChange={pickZone}
          >
            {ZONES.map((zone) => (
              <Toggle
                key={zone.value}
                value={zone.value}
                aria-label={zone.spoken}
              >
                {zone.label}
              </Toggle>
            ))}
          </ToggleGroup>
        )}
      </div>
    </div>
  );
}
